import { useEffect, useState } from "react";

import type { ImageCompressionSource } from "@/constants/analytics/Analytics";
import type { DocumentMimeType } from "@/constants/Documents";
import { ConfirmationAlertTestID, TextTestID } from "@/constants/TestID";
import { AnimationDuration } from "@/constants/UI";
import useAssetsPicker from "@/hooks/useAssetsPicker";
import useImageCompression from "@/hooks/useImageCompression";
import useAppMonitor from "@/services/AppMonitor";
import BottomSheetModal from "@/ui/composed/modal/BottomSheetModal";
import PickFromCameraButton from "@/ui/composed/pickers/PickFromCameraButton";
import PickFromGalleryButton from "@/ui/composed/pickers/PickFromGalleryButton";
import { Button, FAIconName, Text } from "@/ui/core";
import { VStack } from "@/ui/layout";
import { filePickerAssetFromDocumentPickerAsset } from "@/utils/filePicker.util";
import { isWeb } from "@/utils/platform.util";
import { joinTestIDs } from "@/utils/string.util";

interface FilePickerModalProps extends ViewWithTestID {
  documentMimeTypes?: DocumentMimeType[];
  imageCompressionSource: ImageCompressionSource;
  isOpen: boolean;
  onAssetSelected: (asset: FilePickerAsset) => void;
  onDismiss: () => void;
}

enum FilePickerModalContent {
  AccessibilityLabel = "Dismiss File Picker Modal",
  ChooseFromLibraryButton = "Choose from library",
  Description = "Please choose from the options below:",
  TakePhotoButton = "Take a photo",
  Title = "Add image",
  UploadFileButton = "Upload a file",
}

const FilePickerModal = ({
  documentMimeTypes,
  imageCompressionSource,
  isOpen,
  onAssetSelected,
  onDismiss,
  testID,
}: FilePickerModalProps) => {
  const baseTestID = joinTestIDs(testID, ConfirmationAlertTestID.Primary);
  const [shouldOpenFilePicker, setShouldOpenFilePicker] =
    useState<boolean>(false);
  const { pickDocument } = useAssetsPicker();
  const { compressImage } = useImageCompression();

  const { captureException } = useAppMonitor();

  const handleImageAssetPicked = async (originalAsset: FilePickerAsset) => {
    const compressedImage = await compressImage({
      originalAsset,
      source: imageCompressionSource,
    });
    onAssetSelected(compressedImage);
  };

  const handlePickDocument = async () => {
    try {
      const documentAsset = await pickDocument({
        documentMimeTypes,
      });

      if (!documentAsset) {
        return;
      }

      const pickerAsset = filePickerAssetFromDocumentPickerAsset(documentAsset);
      onAssetSelected(pickerAsset);
    } catch (error) {
      captureException(error);
    }
  };

  // Set the state to open the file picker modal after the parent modal dismisses in onDismissAnimationEnd
  // This is needed to avoid the issue of multiple modals not opening one after another
  // Known issue: https://github.com/react-native-modal/react-native-modal#i-cant-show-multiple-modals-one-after-another
  const handlePickFromFile = () => {
    onDismiss();

    // on web, primarily on Safari, deferring the expo-document-picker click across setState/re-render/useEffect evidently becomes a no-op.
    // so we mirror what SMT already does, which is a more immediate and direct invocation, which then works
    if (isWeb()) {
      setTimeout(
        () => void handlePickDocument(),
        AnimationDuration.ModalDismiss,
      );
      return;
    }

    // Set a small timeout to ensure the dismiss function has been called
    setTimeout(() => setShouldOpenFilePicker(true), 50);
  };

  useEffect(() => {
    if (!shouldOpenFilePicker) return;

    // Use a small timeout to ensure the previous modal has fully closed
    const timer = setTimeout(() => {
      void handlePickDocument();
      setShouldOpenFilePicker(false);
    }, 100);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldOpenFilePicker]);

  return (
    <BottomSheetModal
      dismissAccessibilityLabel={FilePickerModalContent.AccessibilityLabel}
      isOpen={isOpen}
      onRequestClose={() => onDismiss()}
      testID={baseTestID}
      title={FilePickerModalContent.Title}
    >
      <VStack gap={12}>
        <Text
          testID={joinTestIDs(baseTestID, `${TextTestID.Description} `)}
          variant="body"
        >
          {FilePickerModalContent.Description}
        </Text>
        <VStack alignItems="center" gap={12} justifyContent="center">
          <PickFromCameraButton
            onAssetPicked={handleImageAssetPicked}
            onPress={onDismiss}
            testID={baseTestID}
            title={FilePickerModalContent.TakePhotoButton}
            variant="secondary"
          />
          {!isWeb() && (
            <PickFromGalleryButton
              onAssetPicked={handleImageAssetPicked}
              onPress={onDismiss}
              testID={baseTestID}
              title={FilePickerModalContent.ChooseFromLibraryButton}
              variant="secondary"
            />
          )}
          <Button
            isFullWidth
            leftIcon={FAIconName.FileArrowUp}
            onPress={handlePickFromFile}
            testID={joinTestIDs(baseTestID, "upload-file")}
            title={FilePickerModalContent.UploadFileButton}
            variant="secondary"
          />
        </VStack>
      </VStack>
    </BottomSheetModal>
  );
};

export { FilePickerModalContent };
export default FilePickerModal;
